/**
 * AuditLogFilters Component
 * Provides date range, event type, and search filters for audit logs
 */

import { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { cardStyles, formStyles } from '../../lib/styles';
import type { EventTypeInfo, AuditLogFilterParams } from '@leal-crm/shared';
import { ENTITY_TYPES } from '@leal-crm/shared';

interface AuditLogFiltersProps {
  eventTypes: EventTypeInfo[];
  onFilterChange: (filters: AuditLogFilterParams) => void;
  initialFilters?: AuditLogFilterParams;
  isLoading?: boolean;
}

export function AuditLogFilters({
  eventTypes,
  onFilterChange,
  initialFilters = {},
  isLoading = false,
}: AuditLogFiltersProps) {
  const [startDate, setStartDate] = useState(initialFilters.startDate || '');
  const [endDate, setEndDate] = useState(initialFilters.endDate || '');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(
    initialFilters.eventTypes || []
  );
  const [entityType, setEntityType] = useState(initialFilters.entityType || '');
  const [search, setSearch] = useState(initialFilters.search || '');

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const applyFilters = () => {
    const filters: AuditLogFilterParams = {
      page: 1, // Reset to first page when filters change
      entityType: 'auth',
    };

    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (selectedEventTypes.length > 0) filters.eventTypes = selectedEventTypes;
    if (search) filters.search = search;
    if (entityType) filters.entityType = entityType as AuditLogFilterParams['entityType'];

    onFilterChange(filters);
  };

  const handleEventTypeToggle = (eventCode: string) => {
    setSelectedEventTypes((prev) =>
      prev.includes(eventCode)
        ? prev.filter((e) => e !== eventCode)
        : [...prev, eventCode]
    );
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedEventTypes([]);
    setEntityType('');
    setSearch('');
    onFilterChange({ page: 1 });
  };

  const hasActiveFilters =
    startDate || endDate || selectedEventTypes.length > 0 || search || entityType;

  // Apply filters when date or event types change
  useEffect(() => {
    applyFilters();
  }, [startDate, endDate, selectedEventTypes, entityType]);

  // Group event types by category
  const groupedEventTypes = eventTypes.reduce<Record<string, EventTypeInfo[]>>(
    (acc, event) => {
      const category = event.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(event);
      return acc;
    },
    {}
  );

  const categoryLabels: Record<string, string> = {
    success: 'Exitosos',
    failure: 'Fallidos',
    security: 'Seguridad',
    management: 'Gestión',
    other: 'Otros',
  };

  return (
    <div className={cardStyles.container}>
      <div className={cardStyles.header}>
        <span className="text-xs font-medium text-gray-900">Filtros</span>
      </div>
      <div className={`${cardStyles.content} space-y-4`}>
      {/* Search */}
      <div>
        <label className={`${formStyles.label} mb-1`}>Buscar</label>
        <Input
          type="text"
          placeholder="Buscar por usuario, email, IP..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={isLoading}
          className="h-8 text-xs"
        />
      </div>

      {/* Entity Type */}
      <div>
        <label className={`${formStyles.label} mb-1`}>Tipo de entidad</label>
        <select
          value={entityType || ''}
          onChange={(e) => setEntityType(e.target.value)}
          className={formStyles.input}
          disabled={isLoading}
        >
          <option value="">Todas</option>
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`${formStyles.label} mb-1`}>Fecha desde</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={isLoading}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <label className={`${formStyles.label} mb-1`}>Fecha hasta</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={isLoading}
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Event Types */}
      <div>
        <label className={`${formStyles.label} mb-2`}>Tipos de eventos</label>
        <div className="space-y-3">
          {Object.entries(groupedEventTypes).map(([category, events]) => (
            <div key={category}>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                {categoryLabels[category] || category}
              </p>
              <div className="flex flex-wrap gap-2">
                {events.map((event) => (
                  <button
                    key={event.code}
                    type="button"
                    onClick={() => handleEventTypeToggle(event.code)}
                    disabled={isLoading}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      selectedEventTypes.includes(event.code)
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    } disabled:opacity-50`}
                  >
                    {event.displayName}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            Limpiar filtros
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}
